import toc from "markdown-toc";
import { GetStaticPaths, GetStaticProps } from "next";
import { MDXRemoteSerializeResult } from "next-mdx-remote";
import { serialize } from "next-mdx-remote/serialize";
import gfm from "remark-gfm";
import SiteChrome from "@chrome/SiteChrome";
import Accelerator from "@components/accelerators/Accelerator";
import AcceleratorsProjectPage from "@components/accelerators/AcceleratorsProjectPage";
import { HtmlPageTitle } from "@components/HtmlPageTitle";
import { MetaDescription } from "@components/MetaDescription";
import acceleratorData from "@content/en/accelerators/accelerators.yaml";
import { SiteHtmlPageTitle } from "@content/en/HtmlTitle";
import { stringToURI } from "@scripts/utils";
import { getREADME } from "@scripts/mdx";

const { accelerators } = acceleratorData;

interface AcceleratorPageProps {
  projectNameSlug: string;
  readmeSource: MDXRemoteSerializeResult;
  readmeTOC: MDXRemoteSerializeResult;
}

const getAccelerator = (projectNameSlug: string): Accelerator | undefined =>
  accelerators.find((a) => stringToURI(a.name) === projectNameSlug);

const AcceleratorPage = ({
  projectNameSlug,
  readmeSource,
  readmeTOC,
}: AcceleratorPageProps) => {
  const selectedAccelerator = getAccelerator(projectNameSlug);

  if (!selectedAccelerator) {
    throw new Error(`Cannot find accelerator for ${projectNameSlug}`);
  }

  return (
    <SiteChrome>
      <HtmlPageTitle
        title={`${selectedAccelerator.name} - Accelerators - ${SiteHtmlPageTitle}`}
      />
      <MetaDescription description={selectedAccelerator.shortDescription} />

      <AcceleratorsProjectPage
        accelerator={selectedAccelerator}
        readmeSource={readmeSource}
        readmeTOC={readmeTOC}
      />
    </SiteChrome>
  );
};

export const getStaticProps: GetStaticProps = async (context) => {
  const projectNameSlug = context.params?.projectName;
  const accelerator = getAccelerator(projectNameSlug?.toString() || "");

  const source = await getREADME(`${accelerator?.id}`);

  const readmeSource = await serialize(source, {
    mdxOptions: { remarkPlugins: [gfm] },
  });
  const readmeTOC = await serialize(
    toc(source, { maxdepth: 3, slugify: stringToURI }).content,
  );

  return {
    props: {
      projectNameSlug,
      readmeSource,
      readmeTOC,
    },
  };
};

export const getStaticPaths: GetStaticPaths = async () => {
  const should404ForSurprisePaths = true;
  const allPaths = {
    paths: accelerators.map((a) => ({
      params: {
        projectName: stringToURI(a.name),
      },
    })),
    fallback: !should404ForSurprisePaths,
  };
  return allPaths;
};

export default AcceleratorPage;
